import { FC, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Link,
  Tooltip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  PlayArrow as RunIcon,
  Place as PlaceIcon,
  Person as PersonIcon,
  Notes as NotesIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  GroupAdd as GroupAddIcon,
  EventAvailable as ScheduleIcon,
  LogoutOutlined as RemoveIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../../utils/api';
import { barnsService, BarnRosterHorse } from '../../services/barnsService';
import { OfflineNotice, useIsOffline } from '../../offline';
import { reminderService } from '../../services/reminderService';
import DueListTable from '../../components/DueListTable';
import BarnFormDialog from './BarnFormDialog';
import AddHorsesToBarnDialog from './AddHorsesToBarnDialog';
import AddAppointmentDialog from '../Appointments/AddAppointmentDialog';
import { useRBAC } from '../../auth/useRBAC';
import { barnsControls } from '../../auth/pageControls';

const AlertChips: FC<{ horse: BarnRosterHorse }> = ({ horse }) => {
  const alerts = Array.isArray(horse.medicalAlerts) ? horse.medicalAlerts : [];
  if (alerts.length === 0) return null;
  const shown = alerts.slice(0, 2);
  const overflow = alerts.length - shown.length;
  return (
    <Box display="inline-flex" gap={0.5} flexWrap="wrap">
      {shown.map((alert) => (
        <Tooltip key={alert.id} title={alert.text}>
          <Chip
            label={alert.isCritical ? 'Critical' : 'Alert'}
            size="small"
            color={alert.isCritical ? 'error' : 'warning'}
            variant={alert.isCritical ? 'filled' : 'outlined'}
          />
        </Tooltip>
      ))}
      {overflow > 0 && <Chip label={`+${overflow}`} size="small" variant="outlined" />}
    </Box>
  );
};

const BarnDetail: FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isOffline = useIsOffline();
  // FOUR different server gates on one header row (INV-108): the barn writes are manage:patients
  // (barns.controller.ts), "Remove from barn" is a PUT /horses/:id on that same slug, "Schedule
  // appointment" is manage:appointments, and "Run Barn Operation" leads to a charge submit
  // (manage:charges). One flag over all five buttons would be wrong three ways.
  const rbac = useRBAC();
  const can = barnsControls(rbac);
  const [editOpen, setEditOpen] = useState(false);
  const [addHorsesOpen, setAddHorsesOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  // Fresh default per dialog open, but STABLE across background re-renders —
  // an inline `new Date()` prop would re-trigger the dialog's prop-sync effect
  // on every barn refetch and clobber a date the user already picked.
  const scheduleDefaultDate = useMemo(() => new Date(), [scheduleOpen]);

  const {
    data: barn,
    isLoading,
    error,
  } = useQuery(['barn', id], () => barnsService.get(id), { enabled: !!id && !isOffline });

  // Care due for the horses stabled here — the barn-batching workflow two
  // clinics asked for ("see which horses are due, by barn, so receptionists can
  // reach out"). Filtered client-side from the clinic-wide due list, which
  // already carries each row's barn.
  const { enqueueSnackbar } = useSnackbar();
  const {
    data: dueRows = [],
    isError: dueError,
    refetch: refetchDue,
  } = useQuery(['reminders', 'due-list', 'barn', id], () => reminderService.getDueList(), {
    enabled: !!id && !isOffline,
  });
  const barnDueRows = dueRows.filter(
    (row) =>
      row.barn?.id === id &&
      !row.declinedAt &&
      (row.status === 'PENDING' || row.status === 'OVERDUE'),
  );

  const archiveMutation = useMutation(
    () => (barn?.isArchived ? barnsService.restore(id) : barnsService.archive(id)),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['barn', id]);
        queryClient.invalidateQueries(['barns']);
      },
    },
  );

  // Remove from barn = clear the horse's barn (dual-write nulls location too).
  const removeHorseMutation = useMutation(
    (horseId: string) => api.put(`/horses/${horseId}`, { barnId: null }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['barn', id]);
        queryClient.invalidateQueries(['barns']);
        queryClient.invalidateQueries(['horses']);
        queryClient.invalidateQueries(['horse-locations']);
      },
    },
  );

  const addressLine = barn
    ? [barn.street, [barn.city, barn.state].filter(Boolean).join(', '), barn.zip]
        .filter(Boolean)
        .join(' · ')
    : '';

  const breedLabel = (horse: BarnRosterHorse) =>
    horse.isMixed && horse.secondBreed ? `${horse.breed} / ${horse.secondBreed}` : horse.breed;

  return (
    <Box sx={{ overflowX: 'hidden', p: { xs: 1, sm: 1.5 } }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/barns')} sx={{ mb: 1 }}>
        Barns
      </Button>

      <OfflineNotice>Barn detail needs a connection.</OfflineNotice>

      {!isOffline && isLoading && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}

      {!isOffline && !!error && <Alert severity="error">Failed to load this barn.</Alert>}

      {!isOffline && barn && (
        <>
          <Box
            display="flex"
            alignItems="flex-start"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={1}
            mb={1.5}
          >
            <Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="h4" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2.125rem' } }}>
                  {barn.name}
                </Typography>
                {barn.isArchived && <Chip label="Archived" />}
              </Box>
              <Typography color="textSecondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                {barn.horses.length} horse{barn.horses.length === 1 ? '' : 's'} ·{' '}
                {barn.clients.length} client{barn.clients.length === 1 ? '' : 's'}
              </Typography>
            </Box>
            <Box display="flex" gap={1} flexWrap="wrap">
              {can.scheduleAppointment && (
                <Button
                  variant="contained"
                  startIcon={<ScheduleIcon />}
                  disabled={barn.isArchived}
                  onClick={() => setScheduleOpen(true)}
                >
                  Schedule appointment
                </Button>
              )}
              {can.runBarnOperation && (
                <Button
                  startIcon={<RunIcon />}
                  disabled={barn.horses.length === 0}
                  onClick={() =>
                    navigate(`/barn-operations?barn=${encodeURIComponent(barn.name)}`)
                  }
                >
                  Run Barn Operation
                </Button>
              )}
              {can.assignHorses && (
                <Button
                  startIcon={<GroupAddIcon />}
                  disabled={barn.isArchived}
                  onClick={() => setAddHorsesOpen(true)}
                >
                  Add horses
                </Button>
              )}
              {can.edit && (
                <Button startIcon={<EditIcon />} onClick={() => setEditOpen(true)}>
                  Edit
                </Button>
              )}
              {can.archive && (
                <Button
                  startIcon={barn.isArchived ? <UnarchiveIcon /> : <ArchiveIcon />}
                  color={barn.isArchived ? 'primary' : 'warning'}
                  onClick={() => archiveMutation.mutate()}
                  disabled={archiveMutation.isLoading}
                >
                  {barn.isArchived ? 'Restore' : 'Archive'}
                </Button>
              )}
            </Box>
          </Box>

          {/* Proportional columns (Contact holds the most content) rather than
              equal thirds, so cards don't force-stretch on wide monitors. */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '0.8fr 1.4fr 0.8fr' },
              gap: 1.5,
              mb: 1.5,
            }}
          >
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                  <PlaceIcon fontSize="small" color="action" />
                  <Typography variant="subtitle2">Address</Typography>
                </Box>
                <Typography variant="body2" color={addressLine ? 'textPrimary' : 'textSecondary'}>
                  {addressLine || 'No address on file'}
                </Typography>
              </CardContent>
            </Card>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                  <PersonIcon fontSize="small" color="action" />
                  <Typography variant="subtitle2">Contact</Typography>
                </Box>
                {(() => {
                  const additional = Array.isArray(barn.contacts) ? barn.contacts : [];
                  const hasPrimary =
                    !!barn.contactName || !!barn.contactPhone || !!barn.contactEmail;
                  if (!hasPrimary && additional.length === 0) {
                    return (
                      <Typography variant="body2" color="textSecondary">
                        No contact on file
                      </Typography>
                    );
                  }
                  // One unified list: primary first (badged), then additional
                  // contacts — each entry separated by a hairline border-top.
                  const entries = [
                    ...(hasPrimary
                      ? [
                          {
                            name: barn.contactName || 'Primary contact',
                            role: null as string | null,
                            phone: barn.contactPhone,
                            email: barn.contactEmail,
                            isPrimary: true,
                          },
                        ]
                      : []),
                    ...additional.map((c) => ({
                      name: c.name,
                      role: c.role ?? null,
                      phone: c.phone,
                      email: c.email,
                      isPrimary: false,
                    })),
                  ];
                  return (
                    <>
                      {entries.map((entry, i) => (
                        <Box
                          key={i}
                          sx={{
                            pt: i === 0 ? 0 : 1,
                            mt: i === 0 ? 0 : 1,
                            borderTop: i === 0 ? 'none' : '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
                            <Typography variant="body2" fontWeight={500}>
                              {entry.name}
                            </Typography>
                            {entry.isPrimary && (
                              <Chip
                                label="Primary"
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '0.65rem',
                                  fontWeight: 600,
                                  bgcolor: 'rgba(139, 69, 19, 0.12)',
                                  color: 'primary.main',
                                }}
                              />
                            )}
                            {entry.role && (
                              <Typography variant="caption" color="textSecondary">
                                · {entry.role}
                              </Typography>
                            )}
                          </Box>
                          {(entry.phone || entry.email) && (
                            <Box
                              display="flex"
                              gap={2}
                              flexWrap="wrap"
                              sx={{ mt: 0.25, color: 'text.secondary' }}
                            >
                              {entry.phone && (
                                <Box display="inline-flex" alignItems="center" gap={0.5}>
                                  <PhoneIcon sx={{ fontSize: '0.9rem' }} />
                                  <Typography variant="body2">{entry.phone}</Typography>
                                </Box>
                              )}
                              {entry.email && (
                                <Box display="inline-flex" alignItems="center" gap={0.5}>
                                  <EmailIcon sx={{ fontSize: '0.9rem' }} />
                                  <Typography variant="body2">{entry.email}</Typography>
                                </Box>
                              )}
                            </Box>
                          )}
                        </Box>
                      ))}
                    </>
                  );
                })()}
              </CardContent>
            </Card>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                  <NotesIcon fontSize="small" color="action" />
                  <Typography variant="subtitle2">Notes</Typography>
                </Box>
                <Typography
                  variant="body2"
                  color={barn.notes ? 'textPrimary' : 'textSecondary'}
                  sx={{ whiteSpace: 'pre-wrap' }}
                >
                  {barn.notes || 'No notes'}
                </Typography>
              </CardContent>
            </Card>
          </Box>

          <Typography variant="h6" sx={{ fontSize: '1rem', mb: 0.5 }}>
            Horses
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Patient ID</TableCell>
                  <TableCell>Breed</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Alerts</TableCell>
                  <TableCell>Owners</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {barn.horses.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography color="textSecondary" align="center" py={2}>
                        No horses at this barn. Set a patient's Barn on the patient form to add
                        it here.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {barn.horses.map((horse) => (
                  <TableRow key={horse.id} hover>
                    <TableCell>
                      <Link
                        component="button"
                        underline="hover"
                        fontWeight={600}
                        onClick={() => navigate(`/patients/${horse.id}`)}
                      >
                        {horse.name}
                      </Link>
                    </TableCell>
                    <TableCell>{horse.patientId || '—'}</TableCell>
                    <TableCell>{breedLabel(horse)}</TableCell>
                    <TableCell>
                      <Chip
                        label={horse.status}
                        size="small"
                        variant="outlined"
                        color={horse.status === 'ACTIVE' ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <AlertChips horse={horse} />
                    </TableCell>
                    <TableCell>
                      {horse.ownerLinks.length > 0
                        ? horse.ownerLinks.map((link, i) => (
                            <span key={link.clientId}>
                              {i > 0 && ', '}
                              <Link
                                component="button"
                                underline="hover"
                                onClick={() =>
                                  navigate('/clients', {
                                    state: { selectedClientId: link.clientId },
                                  })
                                }
                              >
                                {link.name}
                              </Link>
                            </span>
                          ))
                        : (horse.owners || []).map((o) => o.name).join(', ') || '—'}
                    </TableCell>
                    <TableCell align="right">
                      {can.removeHorse && (
                        <Tooltip title="Remove from barn">
                          <span>
                            <Button
                              size="small"
                              color="inherit"
                              startIcon={<RemoveIcon />}
                              onClick={() => removeHorseMutation.mutate(horse.id)}
                              disabled={removeHorseMutation.isLoading}
                            >
                              Remove
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="h6" sx={{ fontSize: '1rem', mb: 0.5 }}>
            Care Due &amp; Overdue
          </Typography>
          {dueError ? (
            <Paper sx={{ p: 3, mb: 3, textAlign: 'center' }}>
              <Typography color="error" gutterBottom>
                Couldn't load care due for this barn — the list may be incomplete.
              </Typography>
              <Button size="small" variant="outlined" onClick={() => refetchDue()}>
                Retry
              </Button>
            </Paper>
          ) : barnDueRows.length === 0 ? (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography color="textSecondary" align="center">
                Nothing due for horses at this barn.
              </Typography>
            </Paper>
          ) : (
            <Paper sx={{ mb: 3 }}>
              <DueListTable
                rows={barnDueRows}
                showBarn={false}
                disableSend={isOffline}
                onChanged={() => refetchDue()}
                onError={(err: any, fallback: string) =>
                  enqueueSnackbar(err?.response?.data?.message || fallback, { variant: 'error' })
                }
              />
            </Paper>
          )}

          <Typography variant="h6" sx={{ fontSize: '1rem', mb: 0.5 }}>
            Clients
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Horses here</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {barn.clients.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography color="textSecondary" align="center" py={2}>
                        No clients — owners appear once horses are assigned to this barn.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {barn.clients.map((client) => (
                  <TableRow key={client.clientId} hover>
                    <TableCell>
                      <Link
                        component="button"
                        underline="hover"
                        fontWeight={600}
                        onClick={() =>
                          navigate('/clients', { state: { selectedClientId: client.clientId } })
                        }
                      >
                        {client.name}
                      </Link>
                    </TableCell>
                    <TableCell>{client.email || '—'}</TableCell>
                    <TableCell>{client.phone || '—'}</TableCell>
                    <TableCell>{client.horseNames.join(', ')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <BarnFormDialog open={editOpen} onClose={() => setEditOpen(false)} barn={barn} />
          <AddHorsesToBarnDialog
            open={addHorsesOpen}
            onClose={() => setAddHorsesOpen(false)}
            barnId={barn.id}
            barnName={barn.name}
          />
          {/* Books ONE barn-level appointment (subject locked to this barn) —
              same flow as the calendar's subject=Barn booking. The old
              per-horse ScheduleBarnVisitDialog was retired with it. */}
          <AddAppointmentDialog
            open={scheduleOpen}
            onClose={() => setScheduleOpen(false)}
            selectedDate={scheduleDefaultDate}
            initialSubject="barn"
            lockSubject
            preSelectedBarnId={barn.id}
            preSelectedBarnName={barn.name}
          />
        </>
      )}
    </Box>
  );
};

export default BarnDetail;
